import { uniq } from 'lodash-es'
import {
  BusinessLogic,
  NormalizedStreamBusinessLogic
} from '@/types/businessLogic'
import { CameraStreamWithDeviceId } from '@/types/camera'
import {
  BlurOptions,
  ConfidenceThreshold,
  DeploymentConfig,
  EventEndpoint,
  GpuSelection,
  InputSource,
  InputSourceType,
  MediaFolderConfig,
  OcrSettings
} from '@/types/deployment'
import { NotificationSettings } from '@/types/notificationSettings'
import { buildBuisnessLogicParameter } from './buildBuisnessLogicParameter'
import { buildEventEndpoints } from './buildEventEndpoints'

const getFrameSize = (resolution: CameraStreamWithDeviceId['resolution']) => {
  if (typeof resolution === 'string') {
    const [height, width] = resolution.split('x') // frame_width and frame_height are swapped h x w
    return { frameWidth: Number(width), frameHeight: Number(height) }
  } else {
    return { frameWidth: resolution.width, frameHeight: resolution.height }
  }
}

type BuildDeploymentDataParams = {
  appId: string
  primaryModelId: string
  secondaryModelId?: string
  width: number
  height: number
  cameraStreams: CameraStreamWithDeviceId[]
  savedStreamLogics: NormalizedStreamBusinessLogic[]
  savedEventEndpoints: EventEndpoint[]
  confidenceThresholds: ConfidenceThreshold[]
  blurOptions: BlurOptions
  eventVideoDurationTime: number
  preEventSeconds: number
  cacheSeconds: number | ''
  inferenceInterval: number
  minDetectionFrames: number
  notificationSettings: NotificationSettings
  ocrSettings: OcrSettings
  inputSource: InputSourceType
  gpuIndex: number | null
  runInGroups: boolean
  gpuSelection: GpuSelection
  // Media folder only
  mediaFolderConfig?: MediaFolderConfig
  selectedDeviceIds?: string[]
}

export const buildDeploymentData = ({
  appId,
  primaryModelId,
  secondaryModelId,
  width,
  height,
  cameraStreams,
  savedStreamLogics,
  savedEventEndpoints,
  confidenceThresholds,
  blurOptions,
  eventVideoDurationTime,
  preEventSeconds,
  cacheSeconds,
  inferenceInterval,
  minDetectionFrames,
  notificationSettings,
  ocrSettings,
  inputSource,
  gpuIndex,
  runInGroups,
  gpuSelection,
  mediaFolderConfig,
  selectedDeviceIds
}: BuildDeploymentDataParams): DeploymentConfig => {
  const isMediaFolder = inputSource === InputSourceType.MediaFolder
  const isGrouped = runInGroups && !isMediaFolder
  const deviceIds = uniq(
    cameraStreams.map((cameraStream) => cameraStream.device_id)
  )

  const streams = cameraStreams.map((cameraStream) => {
    const streamLogics = savedStreamLogics.filter(
      (savedStreamLogic) =>
        savedStreamLogic.camera_id === cameraStream.camera_id
    )

    const businessLogics: BusinessLogic[] = streamLogics.flatMap(
      (streamLogic) => {
        const logicParameters = streamLogic.parameters.map((parameter) => {
          return buildBuisnessLogicParameter({
            type: parameter.type,
            coordinates: parameter.coordinates,
            name: parameter.name,
            alert: parameter.notification,
            upload_video: parameter.upload_video,
            upload_bbox_video: parameter.upload_bbox_video,
            rules: parameter.rules
          })
        })

        return {
          id: streamLogic.logic_id,
          parameters: logicParameters
        }
      }
    )

    const { frameWidth, frameHeight } = getFrameSize(cameraStream.resolution)

    return {
      stream_id: cameraStream.camera_id,
      device_id: cameraStream.device_id,
      frame_width: frameWidth,
      frame_height: frameHeight,
      eglsink: streamLogics.some((logic) => logic.live_output.screen),
      rtspsink: streamLogics.some((logic) => logic.live_output.rtsp),
      business_logics: businessLogics,
      event_endpoints: buildEventEndpoints(savedEventEndpoints, cameraStream)
    }
  })

  const buildInputSource = (): InputSource =>
    isMediaFolder && mediaFolderConfig
      ? {
          type: InputSourceType.MediaFolder,
          // Note: multiple device deployment is not supported for media folder pipeline
          device_id: selectedDeviceIds?.[0] ?? '',
          input_folder_path: mediaFolderConfig.input_folder_path,
          output_folder_path: mediaFolderConfig.output_folder_path,
          delete_processed: mediaFolderConfig.delete_processed
        }
      : { type: InputSourceType.Cameras, streams }

  // A device still on a stored multi-GPU list was never chosen for; the server keeps one of its GPUs.
  const buildGpuSelection = () =>
    deviceIds
      .filter((deviceId) => gpuSelection[deviceId]?.length === 1)
      .map((deviceId) => ({
        device_id: deviceId,
        gpu_indexes: gpuSelection[deviceId]
      }))

  return {
    application_id: appId,
    primary_model_id: primaryModelId,
    secondary_model_id: secondaryModelId,
    ...(isGrouped
      ? {
          run_in_groups: true,
          gpu_selection: buildGpuSelection(),
          group_settings: {
            interval: inferenceInterval,
            pre_event_s: preEventSeconds,
            cache_s: cacheSeconds === '' ? null : cacheSeconds
          }
        }
      : gpuIndex !== null &&
        (isMediaFolder || deviceIds.length === 1) && { gpu_index: gpuIndex }),
    confidence_threshold: confidenceThresholds,
    blur_options: blurOptions,
    input_source: buildInputSource(),
    ...(!isMediaFolder && {
      config_width: width,
      config_height: height,
      event_video_duration_time: eventVideoDurationTime,
      min_detection_frames: minDetectionFrames,
      notification_settings: notificationSettings,
      ocr_settings: ocrSettings
    })
  }
}
